import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Globe, MapPin, Facebook, Instagram, Linkedin } from "lucide-react";
import { COMPANY, NAV_LINKS, SERVICES } from "../mock/mockData";

const Footer = () => {
  return (
    <footer className="relative bg-[#05050A] border-t border-white/5 overflow-hidden" data-testid="site-footer">
      {/* Decorative gold arc */}
      <div
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] pointer-events-none opacity-40"
        aria-hidden="true"
      >
        <div className="gold-arc w-full h-full" />
      </div>

      <div className="container-lux py-20 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <img src={COMPANY.logo} alt="Moon and Back Support" className="h-14 w-14 rounded-full moon-glow" />
              <div>
                <div className="font-serif text-xl text-white">Moon <span className="text-gold">and</span> Back</div>
                <div className="text-[10px] tracking-[0.3em] text-white/60 uppercase">Support</div>
              </div>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed max-w-sm">
              {COMPANY.mission}
            </p>
            <div className="flex gap-4 mt-6">
              <a href="#" className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold transition-colors" data-testid="footer-facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold transition-colors" data-testid="footer-instagram">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold transition-colors" data-testid="footer-linkedin">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Explore */}
          <div className="md:col-span-2">
            <h4 className="text-xs tracking-[0.25em] uppercase text-gold mb-5">Explore</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-white/70 hover:text-gold link-gold transition-colors" data-testid={`footer-link-${link.label.toLowerCase()}`}>
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/portal" className="text-sm text-white/70 hover:text-gold link-gold transition-colors" data-testid="footer-link-portal">
                  Client Portal
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="md:col-span-3">
            <h4 className="text-xs tracking-[0.25em] uppercase text-gold mb-5">Services</h4>
            <ul className="space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.id}>
                  <Link to="/services" className="text-sm text-white/70 hover:text-gold link-gold transition-colors" data-testid={`footer-service-${s.id}`}>
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h4 className="text-xs tracking-[0.25em] uppercase text-gold mb-5">Reach Us</h4>
            <ul className="space-y-4 text-sm text-white/70">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-gold flex-shrink-0" />
                <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="hover:text-gold transition-colors" data-testid="footer-phone">
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-gold flex-shrink-0" />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-gold transition-colors break-all" data-testid="footer-email">
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Globe className="w-4 h-4 mt-0.5 text-gold flex-shrink-0" />
                <span>{COMPANY.website}</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-gold flex-shrink-0" />
                <span>{COMPANY.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/50">
          <div>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-gold transition-colors">Privacy</a>
            <a href="#" className="hover:text-gold transition-colors">Terms</a>
            <a
              href="https://app.emergent.sh/?utm_source=moonandback"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/30 hover:text-gold transition-colors"
              data-testid="footer-emergent-credit"
            >
              Made with Emergent
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
